//------------------------------------------------------------------------------
// BFA10102G 共架申込 基本情報
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field } from 'redux-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import CustomStepper from '@/components/molecules/Stepper';
import { PositiveButton } from '@/components/atoms/Buttons/PositiveButton';
import * as validation from '@/components/validateRule';
import {
  renderSelect,
  renderCheckbox,
  renderTextField,
} from '@/components/atoms/CustomPart';
import { formatStringDate } from '@/common/common';
import address from '@/components/address-fields';
import PoleItem from '@/utils/pole-item';
import pulldown from '@/utils/pulldown-utils';
import TitledArea from '@/components/titled-area';
import TooltipLabel from '@/components/tooltip-label';
import DisplayField from '@/components/display-field';

/**
 * 申込情報パネル
 */
class OrderInfoPanel extends React.Component {
  /**
   * 描画
   */
  render() {
    const {
      values,
      orderNo,
      orderInfo,
      orderPole,
      updatable,
      genericMap,
      kojiHosyokinList,
      gijyutuKentoList,
    } = this.props;

    // 描画
    return (
      <Paper elevation={3} sx={{ px: 3, py: 2 }}>

        {/* 申込進捗 */}
        <Box sx={{ mt: 1 }}>
          <CustomStepper
            category="1"
            status={orderInfo?.orderStatus}
            genericStatus={genericMap?.order_status ?? []}
          />
        </Box>

        {/* 申込情報 */}
        <TitledArea title="申込情報">
          <Grid container spacing={1}>

            {/* 申込番号-版数 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="申込番号"
                value={orderNo?.toString()}
              />
            </Grid>

            {/* 申込元改修依頼番号 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="申込元改修依頼番号"
                value={orderInfo?.kaisyuIraiNo}
              />
            </Grid>

            {/* 申込元変更連絡番号 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="申込元変更連絡番号"
                value={orderInfo?.henkoRenrakuNo}
              />
            </Grid>

            {/* 申込日 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="申込日"
                value={orderInfo?.orderDate}
              />
            </Grid>

            {/* 手持箇所 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="手持箇所"
                value={orderInfo?.temotiKasyo}
              />
            </Grid>

            {/* 申込ステータス */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="申込ステータス"
                value={orderInfo?.orderStatusName}
              />
            </Grid>

            {/* 最終更新日 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="最終更新日"
                value={formatStringDate(orderInfo?.updateTime)}
              />
            </Grid>

            {/* 申込共架事業者 */}
            <Grid item xs={4} md={3}>
              <DisplayField
                label="申込共架事業者"
                value={orderInfo?.zigyosyaName}
              />
            </Grid>

            {/* 共架者申込番号 */}
            <Grid item xs={4} md={3}>
              <Field name="kgOrderNo" label="共架者申込番号"
                fullWidth component={renderTextField}
                required validate={[ validation.required ]}
                inputProps={{ maxLength: 12 }}
                InputProps={{ readOnly: !updatable }}
              />
            </Grid>

            {/* 工事件名 */}
            <Grid item xs={12} md={6}>
              <Field name="kojiKenmei" label="工事件名"
                fullWidth component={renderTextField}
                required validate={[ validation.required ]}
                inputProps={{ maxLength: 50 }}
                InputProps={{ readOnly: !updatable }}
              />
            </Grid>

            {/* 改修工事実施区分(チェックボックス) */}
            <Grid item xs={12} md={6}
              sx={{ mt: 1 }}
            >
              <Field name="autoOrder"
                component={renderCheckbox}
                label={
                  <TooltipLabel
                    label="改修工事の要否によらず自動で共架本申込の処理を実施"
                    icon={<InfoOutlined />}
                  >
                    チェックを入れると共架承認待ち状態となり、
                    改修費用に応じた申込内容の修正や取消が不可となります。
                  </TooltipLabel>
                }
                disabled={!updatable}
              />
            </Grid>
          </Grid>
        </TitledArea>

        {/* 工事場所 */}
        <TitledArea title="工事情報">
          <Grid container spacing={1}>

            {/* 地区 */}
            <Grid item xs={3} sm={3}>
              <DisplayField
                label="地区"
                placeholder="電柱選択"
                value={orderPole?.areaName}
              />
            </Grid>

            {/* 代表電柱 */}
            <Grid item xs={6} md={6}>
              <DisplayField
                label="代表電柱"
                value={PoleItem.longText(orderPole)}
                placeholder="代表電柱を選択してください"
              />
            </Grid>

            {/* 電柱選択 */}
            <Grid item xs={3} md={3}
              sx={{ display: 'flex', alignItems: 'end' }}
            >
              <PositiveButton
                variant="contained" size="large"
                onClick={this.props.handlePoleSelect}
              >
                電柱選択
              </PositiveButton>
            </Grid>

            {/* 都道府県 */}
            <Grid item xs={6} sm={3}>
              <address.TodofukenField
                name="kojiPrefectures" label="都道府県"
                genericMap={genericMap}
                withEmpty={true}
                required validate={[ validation.required ]}
                InputProps={{ readOnly: !updatable }}
                onChange={this.handleChangeTodofuken}
              />
            </Grid>

            {/* 市区町村 */}
            <Grid item xs={6} sm={5}>
              <address.SikutyosonField
                name="kojiMunicipalities" label="市区町村"
                inputValue={values?.kojiMunicipalities}
                upperValue={values?.kojiPrefectures}
                withEmpty={true}
                required validate={[ validation.required ]}
                InputProps={{ readOnly: !updatable }}
                onChange={this.handleChangeSikutyoson}
              />
            </Grid>

            {/* 大字通称 */}
            <Grid item xs={6} sm={4}>
              <address.OazatusyoField
                name="kojiOazatusyo" label="大字通称"
                inputValue={values?.kojiOazatusyo}
                upperValue={values?.kojiMunicipalities}
                withEmpty={true}
                InputProps={{ readOnly: !updatable }}
                onChange={this.handleChangeOazatusyo}
              />
            </Grid>

            {/* 字丁目 */}
            <Grid item xs={6} sm={3}>
              <address.AzatyomeField
                name="kojiAzatyome" label="字丁目"
                inputValue={values?.kojiAzatyome}
                upperValue={values?.kojiOazatusyo}
                withEmpty={true}
                InputProps={{ readOnly: !updatable }}
              />
            </Grid>

            {/* ビル名等 */}
            <Grid item xs={12} sm={9}>
              <Field name="kojiBirumei" label="ビル名等"
                fullWidth component={renderTextField}
                inputProps={{ maxLength: 30 }}
                InputProps={{ readOnly: !updatable }}
              />
            </Grid>

            {/* 工事補償金請求先 */}
            <Grid item xs={12} lg={6}>
              <Field name="kojiHosyokinBill" label="工事補償金請求先"
                fullWidth component={renderSelect}
                required validate={[ validation.required ]}
                InputProps={{ readOnly: !updatable }}
              >
                {[
                  pulldown.EMPTY_ITEM,
                  ...pulldown.makeItems(kojiHosyokinList,
                      { value: values?.kojiHosyokinBill }),
                ]}
              </Field>
            </Grid>

            {/* 技術検討費用請求先 */}
            <Grid item xs={12} lg={6}>
              <Field name="gijyutuKentoBill" label="技術検討費用請求先"
                fullWidth component={renderSelect}
                required validate={[ validation.required ]}
                InputProps={{ readOnly: !updatable}}
              >
                {[
                  pulldown.EMPTY_ITEM,
                  ...pulldown.makeItems(gijyutuKentoList,
                      { value: values?.gijyutuKentoBill }),
                ]}
              </Field>
            </Grid>

            {/* 着工予定日 */}
            <Grid item xs={6} sm={4}>
              <Field name="tyakkoYoteiDate" label="着工予定日"
                component={renderTextField} type="date"
                required validate={[ validation.required ]}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: !updatable }}
              />
            </Grid>

            {/* しゅん工予定日 */}
            <Grid item xs={6} sm={4}>
              <Field name="syunkoYoteiDate" label="しゅん工予定日"
                component={renderTextField} type="date"
                required validate={[ validation.required ]}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: !updatable }}
              />
            </Grid>
          </Grid>
        </TitledArea>
      </Paper>
    );
  }

  // 都道府県変更 - 市区町村/大字通称/字丁目クリア
  handleChangeTodofuken = () => {
    this.props.change('kojiMunicipalities', '');
    this.props.change('kojiOazatusyo', '');
    this.props.change('kojiAzatyome', '');
  }

  // 市区町村変更 - 大字通称/字丁目クリア
  handleChangeSikutyoson = () => {
    this.props.change('kojiOazatusyo', '');
    this.props.change('kojiAzatyome', '');
  }

  // 大字通称変更 - 字丁目クリア
  handleChangeOazatusyo = () => {
    this.props.change('kojiAzatyome', '');
  }
}

// プロパティ
OrderInfoPanel.propTypes = {
  orderNo:          PropTypes.object,    // 申込番号
  orderInfo:        PropTypes.object,    // 申込情報
  updatable:        PropTypes.bool,      // 更新可否
  kojiHosyokinList: PropTypes.array.isRequired,   // 工事補償金請求先リスト
  gijyutuKentoList: PropTypes.array.isRequired,   // 技術検討費用請求先リスト
  handlePoleSelect: PropTypes.func.isRequired,    // 電柱選択ボタン処理

  // フォーム
  values: PropTypes.object,             // フォーム入力値
  change: PropTypes.func.isRequired,    // フォーム変更関数

  // ストア
  genericMap: PropTypes.object,   // 汎用マスタ
};

// ストア接続
const mapStateToProps = (state) => ({
  genericMap: state.master.genericMap,
});
const mapDispatchToProps = {};
OrderInfoPanel = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrderInfoPanel);

// デフォルトエクスポート
export default OrderInfoPanel;
